import React from 'react';

interface TermsAndConditionsProps {
  appName?: string;
  onClose: () => void;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ appName = 'Our Application', onClose }) => {
  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[310] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[900px] mx-auto px-6 py-32 text-white/80">
        <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-4 italic text-white flex flex-col gap-2">
          <span>Terms &</span>
          <span>Conditions</span>
        </h1>
        <p className="text-xl text-primary font-bold tracking-widest uppercase mb-4">
          {appName}
        </p>
        <p className="text-sm font-bold tracking-widest uppercase mb-12 border-b border-white/10 pb-6 text-white/40">
          Last updated: [DATE]
        </p>

        <div className="space-y-12 font-light leading-relaxed text-lg">
          <section className="space-y-4">
            <p>
              These Terms and Conditions ("Terms") govern your access to and use of the {appName} mobile application and any related services (collectively, the "Service"), operated by T.H.E Apps, a company registered in [Republic of South Africa] ("Company", "we", "us"). By downloading, accessing, or using {appName}, you agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>
            <p>
              {appName} is intended for use by parents, guardians, and other adults coordinating carpool and ride-sharing arrangements for children and family members. If you are using the Service on behalf of a family, club, or group, you confirm you have the authority to agree to these Terms on their behalf where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">1. Description of Service</h2>
            <p>
              {appName} is a carpool coordination application that allows users to create and join "clubs" or groups, build shared driving schedules, assign drivers, manage ride availability, and receive notifications related to organized carpooling. The Service is provided for personal, non-commercial coordination purposes among consenting participants.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">2. Eligibility and Accounts</h2>
            <div className="space-y-4">
              <p>
                You must be at least 18 years old to create an account and act as a "Parent/Driver" user on {appName}. The Service is not directed at children under 13, and children do not independently register for or operate accounts. Information relating to minors (such as names or schedules) is entered and managed by a parent or guardian and is handled in accordance with our Privacy Policy, available at [PRIVACY POLICY URL].
              </p>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to provide accurate information when creating your account and to keep it up to date.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">3. Subscriptions and Billing</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.1 Subscription Plans</h3>
                <p className="mb-3">
                  Certain features of {appName} ("{appName} Pro") require a paid subscription. Available plans and current pricing are displayed within the app prior to purchase and may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Monthly</strong> — billed every month</li>
                  <li><strong>Annual</strong> — billed every 12 months</li>
                </ul>
                <p className="mt-3">
                  Prices are displayed in your local currency where supported and may vary by region. All subscriptions are auto-renewing unless canceled by you prior to the end of the current billing period.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.2 Free Trial</h3>
                <p>
                  New subscribers may be offered a free trial period (currently 14 days) prior to being charged. If you do not cancel before the trial period ends, your chosen subscription will automatically begin and you will be charged the then-current price for the plan you selected. You may cancel at any time during the trial to avoid being charged.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.3 Payment Processing</h3>
                <p>
                  All purchases are processed through the Apple App Store (and, where applicable, Google Play). Payment will be charged to your Apple ID or Google account at confirmation of purchase. Your subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current billing period. Your account will be charged for renewal within 24 hours prior to the end of the current period, at the price you agreed to at purchase, unless the price has changed and you have been notified in accordance with applicable law and platform requirements.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.4 Managing and Canceling Your Subscription</h3>
                <p>
                  You can manage or cancel your subscription at any time through your Apple ID account settings (Settings → [Your Name] → Subscriptions) or the equivalent Google Play settings, or via the "Manage Subscription" option within the app. Canceling stops future renewals but does not refund the current billing period, except as described below.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.5 Refunds</h3>
                <p>
                  Refund requests are handled by Apple or Google, in accordance with their respective refund policies, as they are the merchant of record for in-app purchases. We do not directly process payments or issue refunds. To request a refund, please contact Apple Support or Google Play Support directly.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.6 Price Changes</h3>
                <p>
                  We may change subscription prices from time to time. Any price change will apply no earlier than your next renewal following the change, and, where required by law or platform policy, you will be notified in advance and given the opportunity to cancel before the new price takes effect.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">4. Founding Family / Promotional Access</h2>
            <p>
              From time to time, we may offer complimentary lifetime or promotional access to early users or as part of a limited-time program. Eligibility for any such program is determined solely by us, is non-transferable, and may be modified or discontinued at our discretion for future users, without affecting access already granted.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">5. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of these Terms;</li>
              <li>Attempt to gain unauthorized access to other users' accounts, clubs, or data;</li>
              <li>Use the Service to harass, endanger, or misrepresent your identity to other users, including in the context of arranging transportation for children;</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the app, except where permitted by law;</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
            </ul>
            <p className="mt-4">
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">6. User Responsibility for Ride Arrangements</h2>
            <p>
              {appName} is a scheduling and coordination tool only. <strong className="text-white">We are not a transportation provider, are not involved in the actual provision of any ride, and do not vet, screen, or supervise drivers or passengers.</strong> All arrangements made using the Service — including who drives, who rides, timing, and safety of the journey — are solely the responsibility of the participating users. You are solely responsible for verifying the identity, licensing, insurance, and suitability of any driver you arrange rides with through the Service, including other members of your household or club. We disclaim all liability for any incident, injury, loss, or damage arising from or related to rides arranged using the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">7. User Content</h2>
            <p>
              Any information you submit to the Service (schedules, club names, member details, messages, etc.) remains your responsibility. You represent that you have the right to share any information you input about other individuals, including other adults and minors in your care, and that you will do so only for the purposes of coordinating carpooling.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">8. Intellectual Property</h2>
            <p>
              The Service, including its design, features, logos, and underlying code, is owned by T.H.E Apps and protected by applicable intellectual property laws. Your subscription grants you a limited, non-exclusive, non-transferable license to use the app for its intended purpose. No other rights are granted.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">9. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without notice, for conduct that violates these Terms or is otherwise harmful to other users or the Service. You may stop using the Service and delete your account at any time via the in-app account deletion option in Settings.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">10. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available," without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">11. Limitation of Liability</h2>
            <div className="space-y-4">
              <p>
                To the maximum extent permitted by law, T.H.E Apps shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, revenue, or profits, arising out of or related to your use of the Service, even if advised of the possibility of such damages. Our total liability for any claim arising from these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
              <p>
                Nothing in these Terms limits liability that cannot be limited under applicable law, including liability for death or personal injury caused by negligence, or liability that cannot be excluded under South African consumer protection law where applicable.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless T.H.E Apps and its officers, employees, and affiliates from any claims, damages, losses, or expenses (including reasonable legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party, including in connection with rides arranged through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be notified via the app or by updating the "Last updated" date above. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">14. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the Republic of South Africa, without regard to conflict of law principles, unless a different governing law is required by the mandatory consumer protection laws of your country of residence.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">15. Contact Us</h2>
            <p className="mb-4">
              If you have questions about these Terms, please contact us at:
            </p>
            <ul className="list-none space-y-2 bg-white/5 p-6 border border-white/10 rounded-sm">
              <li><strong className="text-white">Email:</strong> <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a></li>
              <li><strong className="text-white">Company:</strong> T.H.E Apps</li>
              <li><strong className="text-white">Address:</strong> 94 9th avenue parkmore sandton 2196</li>
            </ul>
          </section>

          <section className="pt-12 border-t border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Appendix: Apple App Store Additional Terms</h2>
            <p className="mb-4">
              If you downloaded the app from the Apple App Store, you acknowledge and agree that:
            </p>
            <ol className="list-decimal pl-6 space-y-4">
              <li>These Terms are between you and T.H.E Apps only, not with Apple Inc. ("Apple"), and Apple is not responsible for the Service or its content.</li>
              <li>Apple has no obligation to furnish any maintenance or support services for the Service.</li>
              <li>In the event of any failure of the Service to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any) for the app to you; to the maximum extent permitted by law, Apple has no other warranty obligation with respect to the Service.</li>
              <li>Apple is not responsible for addressing any claims by you or any third party relating to the Service, including product liability claims, claims that the Service fails to conform to legal or regulatory requirements, and claims under consumer protection or similar legislation.</li>
              <li>In the event of any third-party claim that the Service infringes that third party's intellectual property rights, T.H.E Apps, not Apple, is solely responsible for the investigation, defense, settlement, and discharge of such claim.</li>
              <li>You represent that you are not located in a country subject to a U.S. Government embargo or designated as a "terrorist supporting" country, and that you are not on any U.S. Government list of prohibited or restricted parties.</li>
              <li>Apple and Apple's subsidiaries are third-party beneficiaries of these Terms, and upon your acceptance, Apple has the right to enforce these Terms against you as a third-party beneficiary.</li>
            </ol>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
